/**
 * @module @yimoka/i18n/bundled/registry
 * @summary bundled 语言包注册表
 * @author keep
 * @since 2026-05-19
 */

import { isVacuous } from '@yimoka/shared';

import type { BundledMap, NamespaceMap } from '../types';

/** 注册表实例 */
export interface BundledRegistry {
  /** 默认语种短码 */
  defaultLng: string;
  /** 获取某语种全部 namespace 资源（i18next resources 格式） */
  getResourcesForLang: (lang: string) => NamespaceMap;
  /** 获取某语种某 namespace，非默认语种空时回退默认语种 */
  getNamespace: (lang: string, ns: string) => Record<string, unknown>;
  /** 全部 bundled 原始数据 */
  bundled: BundledMap;
}

/**
 * 创建 bundled 注册表。
 *
 * @param bundled - 语言 → namespace → 文案
 * @param defaultLng - 默认语种，缺省取 bundled 首键
 * @returns 注册表
 */
export function createBundledRegistry<T extends BundledMap>(
  bundled: T,
  defaultLng?: keyof T & string,
): BundledRegistry {
  const fallbackLng = defaultLng ?? (Object.keys(bundled)[0] as string);

  return {
    defaultLng: fallbackLng,
    bundled,

    getResourcesForLang(lang: string): NamespaceMap {
      const langData = bundled[lang];
      if (!langData) {
        return {};
      }
      return { ...langData };
    },

    getNamespace(lang: string, ns: string): Record<string, unknown> {
      const langData = bundled[lang];
      const nsData = langData?.[ns];
      if (!isVacuous(nsData) && typeof nsData === 'object') {
        return nsData as Record<string, unknown>;
      }

      if (lang !== fallbackLng) {
        const fallbackNs = bundled[fallbackLng]?.[ns];
        if (!isVacuous(fallbackNs) && typeof fallbackNs === 'object') {
          return fallbackNs as Record<string, unknown>;
        }
      }

      return {};
    },
  };
}
